import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { fetchWhatsapp, setWhatsappPaused, syncWhatsappTemplates } from "../../Helpers/notificationConsoleApi";
import { eventName, whenText } from "./shared";

const MODE_TEXT = {
    off: { name: "Off", text: "Nothing is queued for WhatsApp. Push and the inbox work as usual." },
    shadow: { name: "Shadow", text: "Messages are built and written to the delivery log, but nothing is sent. Use this to see what would go out." },
    allowlist: { name: "Test numbers only", text: "Only the numbers on the allow-list are messaged. Use this for the first live test." },
    live: { name: "Live", text: "Every customer who agreed to WhatsApp messages is messaged." },
};

const STATE_CLASS = { APPROVED: "bg-success-subtle text-success", FLAGGED: "bg-warning-subtle text-warning-emphasis", UNKNOWN: "bg-secondary-subtle text-secondary" };

// WhatsApp at a glance: which mode it is in, what is set up, how many were sent today, whether each template is
// approved by Meta, and a pause switch. The mode itself is a server setting (WHATSAPP_MODE), not changed here.
const WhatsAppTab = () => {
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [confirmPause, setConfirmPause] = useState(false);

    const load = useCallback(async () => {
        const result = await fetchWhatsapp();
        if (result.ok) setInfo(result.data);
        else toast.error(result.message);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const sync = async () => {
        setBusy("sync");
        const result = await syncWhatsappTemplates();
        setBusy("");
        if (!result.ok) return toast.error(result.message);
        toast.success(result.data.problems?.length ? `Checked ${result.data.fetched} templates. ${result.data.problems.length} need attention.` : `Checked ${result.data.fetched} templates. All good.`);
        return load();
    };

    const pause = async (paused) => {
        setBusy("pause");
        setConfirmPause(false);
        const result = await setWhatsappPaused(paused);
        setBusy("");
        if (!result.ok) return toast.error(result.message);
        toast.success(paused ? "WhatsApp sending is paused" : "WhatsApp sending is back on");
        return load();
    };

    if (loading && !info) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={90} className="mb-3" />
                <Skeleton height={160} />
            </div>
        );
    }
    if (!info) return <div className="settings-card"><div className="nc-empty">Could not load. <button type="button" className="notif-link" onClick={load}>Try again</button></div></div>;

    const mode = MODE_TEXT[info.mode] || MODE_TEXT.off;
    const setup = [
        ["Access token", info.settings.token],
        ["Send address", info.settings.url],
        ["Account id (for template checks)", info.settings.wabaId],
        ["App secret (for delivery updates)", info.settings.appSecret],
        ["Webhook verify token", info.settings.verifyToken],
    ];

    return (
        <div className="settings-card">
            <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h5 className="fw-semibold mb-1">WhatsApp</h5>
                    <p className="text-muted small mb-0">Booking and trip messages through WhatsApp, only to customers who agreed to them.</p>
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load}>Refresh</button>
            </div>

            {info.paused && <div className="nc-find nc-find-problem mb-3">Sending is paused{info.pausedAt ? ` since ${whenText(info.pausedAt)}` : ""}. Messages wait up to 6 hours and are then dropped.</div>}
            {info.authProblem && <div className="nc-find nc-find-problem mb-3">Meta refused the access token {whenText(info.authProblem.at)}. Create a new system-user token and update it in the server settings.</div>}
            {info.problems > 0 && <div className="nc-find nc-find-problem mb-3">{info.problems} template(s) are not approved. Messages that use them are not being sent.</div>}

            <div className="nc-health-grid">
                <div className="nc-stat">
                    <div className="nc-stat-label">Mode</div>
                    <div className="nc-stat-value">{mode.name}</div>
                    <div className="nc-sub">{mode.text}</div>
                </div>
                <div className="nc-stat">
                    <div className="nc-stat-label">Sent today</div>
                    <div className="nc-stat-value">{info.sentToday}</div>
                    <div className="nc-sub">Limit {info.dailyCap} a day, up to {info.settings.ratePerSec} a second</div>
                </div>
                <div className="nc-stat">
                    <div className="nc-stat-label">Agreed to WhatsApp</div>
                    <div className="nc-stat-value">{info.consentedCustomers}</div>
                    <div className="nc-sub">Customers who said yes</div>
                </div>
                {info.mode === "allowlist" && (
                    <div className="nc-stat">
                        <div className="nc-stat-label">Test numbers</div>
                        <div className="nc-stat-value">{info.settings.allowlistCount}</div>
                        <div className="nc-sub">On the allow-list</div>
                    </div>
                )}
            </div>

            <h6 className="fw-semibold mt-4 mb-2">Set up</h6>
            <ul className="nc-devices">
                {setup.map(([label, ok]) => <li key={label}>{label}: <strong className={ok ? "text-success" : "text-muted"}>{ok ? "set" : "not set"}</strong></li>)}
            </ul>
            <div className="nc-sub">These are server settings. Change the mode or the keys in the server environment, then restart the Admin backend.</div>

            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-4 mb-2">
                <h6 className="fw-semibold mb-0">Templates{info.templatesSyncedAt ? <span className="nc-sub ms-2">checked {whenText(info.templatesSyncedAt)}</span> : null}</h6>
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={sync} disabled={busy === "sync" || !info.templateGate}>{busy === "sync" ? "Checking..." : "Check with Meta now"}</button>
            </div>
            {!info.templateGate && <div className="nc-note mb-2">The Meta account id is not set, so approval is not checked and every template is allowed through.</div>}
            <div className="nc-table-wrap">
                <table className="table table-sm align-middle nc-table">
                    <thead><tr><th>Template</th><th>Used for</th><th>Status</th></tr></thead>
                    <tbody>
                        {info.templates.map((template) => (
                            <tr key={template.name}>
                                <td className="nc-mono">{template.name}</td>
                                <td className="nc-sub">{template?.events?.map(eventName)?.join(", ")}</td>
                                <td>
                                    <span className={`nc-status ${STATE_CLASS[template.status] || "bg-danger-subtle text-danger"}`}>{template?.status?.toLowerCase()}</span>
                                    {template.category && template.category !== "UTILITY" ? <span className="nc-tag ms-1">{template?.category?.toLowerCase()}</span> : null}
                                    {template.reason ? <div className="nc-sub">{template.reason}</div> : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <h6 className="fw-semibold mt-4 mb-2">Pause sending</h6>
            <p className="nc-sub mb-2">Stops every WhatsApp message at once, for example if a wrong message is going out. Push and the inbox are not affected.</p>
            {info.paused ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => pause(false)} disabled={busy === "pause"}>{busy === "pause" ? "Saving..." : "Resume sending"}</button>
            ) : confirmPause ? (
                <div className="d-flex gap-2 align-items-center">
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => pause(true)} disabled={busy === "pause"}>Yes, pause WhatsApp</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmPause(false)}>Cancel</button>
                </div>
            ) : (
                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setConfirmPause(true)} disabled={info.mode === "off"}>Pause WhatsApp</button>
            )}
        </div>
    );
};

export default WhatsAppTab;
